import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, X } from 'lucide-react';
import { instructorApi } from '../api/instructor';
import { useUiStore } from '../store/ui';
import type {
  StartingEvaluation,
  StartingEvalQuestion,
  StartingEvalSection,
} from '../types';

const LEVEL_META: Record<
  string,
  { label: string; tone: string }
> = {
  bon: { label: 'Bon', tone: 'bg-green-50 text-green-700 border-green-200' },
  satisfaisant: {
    label: 'Satisfaisant',
    tone: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  faible: { label: 'Faible', tone: 'bg-red-50 text-red-700 border-red-200' },
};

/** Réponse de l'élève mise en forme (contexte lecture seule). */
function studentAnswerLabel(
  q: StartingEvalQuestion,
  raw: string | string[] | undefined,
): string {
  if (raw == null || (Array.isArray(raw) && raw.length === 0)) return '—';
  const labelOf = (v: string) =>
    q.options?.find((o) => o.value === v)?.label ?? v;
  if (Array.isArray(raw)) return raw.map(labelOf).join(', ');
  if (q.type === 'text') return raw;
  return labelOf(raw);
}

function InstructorQuestion({
  q,
  value,
  onChange,
}: {
  q: StartingEvalQuestion;
  value: string | undefined;
  onChange: (v: string) => void;
}) {
  return (
    <div className="py-3 border-t border-slate-100 first:border-t-0 first:pt-0">
      <p className="text-sm font-medium text-slate-800 mb-2">{q.label}</p>
      <div className="flex flex-wrap gap-2">
        {q.options?.map((opt) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition ${
                active
                  ? 'bg-indigo-600 border-indigo-600 text-white'
                  : 'bg-white border-slate-200 text-slate-600 hover:border-indigo-300'
              }`}
              title={opt.points != null ? `${opt.points > 0 ? '+' : ''}${opt.points} pt` : undefined}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      {q.hint && <p className="text-xs text-slate-400 mt-1.5">{q.hint}</p>}
    </div>
  );
}

function Body({ studentId, data }: { studentId: string; data: StartingEvaluation }) {
  const queryClient = useQueryClient();
  const [answers, setAnswers] = useState<Record<string, string>>(
    data.instructorAnswers ?? {},
  );
  // Recharge si la donnée serveur change (autre onglet).
  useEffect(() => {
    setAnswers(data.instructorAnswers ?? {});
  }, [data.instructorAnswers]);

  const mutation = useMutation({
    mutationFn: () => instructorApi.setStudentStartingEvaluation(studentId, answers),
    onSuccess: (fresh) => {
      queryClient.setQueryData(
        ['student', studentId, 'starting-evaluation'],
        fresh,
      );
    },
  });

  const setAnswer = (code: string, v: string) =>
    setAnswers((prev) => ({ ...prev, [code]: v }));

  const total = data.instructorSections.reduce(
    (n, s) => n + s.questions.length,
    0,
  );
  const filled = data.instructorSections.reduce(
    (n, s) => n + s.questions.filter((q) => answers[q.code]).length,
    0,
  );

  if (data.status !== 'completed') {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-center text-sm text-amber-800">
        L'élève n'a pas encore réalisé sa partie de l'évaluation de départ.
        Vous pourrez renseigner les rubriques pratiques une fois qu'il l'aura
        complétée.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Résultat final */}
      {data.final && (
        <div className="rounded-2xl border border-indigo-100 bg-indigo-50 p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">
                Résultat final
              </p>
              <p className="text-2xl font-extrabold text-indigo-700 leading-tight">
                {data.final.score > 0 ? '+' : ''}
                {data.final.score} pts
              </p>
            </div>
            <span
              className={`px-3 py-1 rounded-full border text-sm font-semibold ${
                LEVEL_META[data.final.level]?.tone ?? ''
              }`}
            >
              {LEVEL_META[data.final.level]?.label ?? data.final.level}
            </span>
          </div>
          <div className="mt-3 pt-3 border-t border-indigo-100 text-sm text-slate-700">
            <span className="font-semibold">Formation conseillée :</span>{' '}
            {data.final.proposedTotalHours} h au total, dont{' '}
            {data.final.proposedPracticeHours} h de conduite pratique.
          </div>
          <p className="text-[11px] text-slate-400 mt-1">
            Barème C.F.A — quiz {data.final.knowledgePoints > 0 ? '+' : ''}
            {data.final.knowledgePoints} pts, pratique{' '}
            {data.final.practicalPoints > 0 ? '+' : ''}
            {data.final.practicalPoints} pts.
          </p>
        </div>
      )}

      {/* Rubriques pratiques à noter */}
      {data.instructorSections.map((section: StartingEvalSection) => (
        <div
          key={section.code}
          className="rounded-2xl border border-slate-200 p-4"
        >
          <h3 className="text-sm font-semibold text-slate-900">
            {section.title}
          </h3>
          {section.description && (
            <p className="text-xs text-slate-500 mt-0.5">
              {section.description}
            </p>
          )}
          <div className="mt-2">
            {section.questions.map((q) => (
              <InstructorQuestion
                key={q.code}
                q={q}
                value={answers[q.code]}
                onChange={(v) => setAnswer(q.code, v)}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Contexte : réponses de l'élève */}
      <details className="rounded-2xl border border-slate-200 p-4">
        <summary className="text-sm font-semibold text-slate-800 cursor-pointer">
          Réponses de l'élève (pour information)
        </summary>
        <div className="mt-3 space-y-3">
          {data.definition.sections.map((section) => (
            <div key={section.code}>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                {section.title}
              </p>
              <ul className="space-y-1">
                {section.questions.map((q) => (
                  <li
                    key={q.code}
                    className="flex items-baseline justify-between gap-3 text-sm"
                  >
                    <span className="text-slate-600 flex-1 min-w-0">
                      {q.label}
                    </span>
                    <span className="text-slate-900 font-medium text-right flex-shrink-0 max-w-[45%]">
                      {studentAnswerLabel(q, data.answers[q.code])}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </details>

      <div className="flex items-center justify-end gap-3">
        {mutation.isSuccess && (
          <span className="text-sm text-emerald-600">Enregistré ✓</span>
        )}
        <button
          type="button"
          disabled={mutation.isPending || filled === 0}
          onClick={() => mutation.mutate()}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors"
        >
          {mutation.isPending && <Loader2 size={16} className="animate-spin" />}
          Enregistrer l'évaluation ({filled}/{total})
        </button>
      </div>
    </div>
  );
}

export default function EvaluationModal() {
  const studentId = useUiStore((s) => s.evalStudentId);
  const close = useUiStore((s) => s.closeEvaluation);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['student', studentId, 'starting-evaluation'],
    queryFn: () => instructorApi.getStudentStartingEvaluation(studentId as string),
    enabled: !!studentId,
  });

  if (!studentId) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 max-h-[90vh] overflow-y-auto animate-fade-in">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-slate-900 text-lg font-semibold">
            Évaluation de départ
          </h2>
          <button
            onClick={close}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label="Fermer"
          >
            <X size={18} className="text-slate-500" />
          </button>
        </div>
        <div className="px-6 py-5">
          {isLoading && <p className="text-slate-500">Chargement…</p>}
          {isError && (
            <p className="text-red-600">Impossible de charger l'évaluation.</p>
          )}
          {data && data.status === 'not_requested' ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center text-slate-500 text-sm">
              Aucune évaluation de départ n'a été demandée pour cet élève.
            </div>
          ) : data ? (
            <Body studentId={studentId} data={data} />
          ) : null}
        </div>
      </div>
    </div>
  );
}
